import { signInWithCustomToken, User } from "firebase/auth";
import {
  doc,
  DocumentReference,
  getDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useFirestore } from "reactfire";
import Header from "../../components/Header";
import Loader from "../../components/Loader";
import { auth } from "../../config/firebase";

interface AGMResponse {
  uid: string;
  attending: "self" | "representative" | "not-attending";
  representativeName?: string;
  representativeEmail?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

const TIMEZONES = [
  { location: "London", time: "2:00 PM", zone: "BST" },
  { location: "Sydney", time: "11:00 PM", zone: "AEST" },
  { location: "Singapore", time: "9:00 PM", zone: "SGT" },
  { location: "New York", time: "9:00 AM", zone: "EDT" },
  { location: "San Francisco", time: "6:00 AM", zone: "PDT" },
];

const GOOGLE_CALENDAR_LINK = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=Hitchhiker%20AGM%202026&dates=20260616T130000Z/20260616T140000Z&details=Annual%20General%20Meeting%20for%20Hitchhiker%20Fund&location=https%3A%2F%2Fmeet.google.com%2Fxph-bhoc-ihf`;

export default function AGMScreen() {
  const params = new URLSearchParams(window.location.search);
  const authToken = params.get("authToken");
  const token = params.get("token");
  const firestore = useFirestore();

  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const [attending, setAttending] = useState<
    "self" | "representative" | "not-attending"
  >("self");
  const [representativeName, setRepresentativeName] = useState("");
  const [representativeEmail, setRepresentativeEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [existingResponse, setExistingResponse] = useState<AGMResponse | null>(
    null,
  );
  const [isLoadingResponse, setIsLoadingResponse] = useState(true);

  useEffect(() => {
    return auth.onAuthStateChanged(setUser);
  }, []);

  useEffect(() => {
    if (auth.currentUser || isAuthenticating) {
      return;
    }

    if (authToken) {
      setIsAuthenticating(true);
      signInWithCustomToken(auth, authToken)
        .then(() => {
          console.log("Successfully signed in with custom token");
        })
        .catch((error) => {
          console.error("Error signing in with custom token:", error);
          setAuthError("Failed to authenticate. Please try again.");
        })
        .finally(() => {
          setIsAuthenticating(false);
        });
      return;
    }

    if (token) {
      setIsAuthenticating(true);
      fetch(
        "https://us-central1-deals-7301a.cloudfunctions.net/exchangeAGMAuthToken",
        {
          body: JSON.stringify({ token }),
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
        },
      )
        .then(async (response) => {
          if (!response.ok) {
            throw new Error("Failed to exchange auth token");
          }

          const data: { authToken?: string } = await response.json();

          if (!data.authToken) {
            throw new Error("No auth token returned");
          }

          return signInWithCustomToken(auth, data.authToken);
        })
        .then(() => {
          console.log("Successfully signed in with exchanged custom token");
        })
        .catch((error) => {
          console.error("Error exchanging AGM auth token:", error);
          setAuthError("Failed to authenticate. Please try again.");
        })
        .finally(() => {
          setIsAuthenticating(false);
        });
    }
  }, [authToken, isAuthenticating, token]);

  useEffect(() => {
    if (user) {
      const responseDoc = doc(
        firestore,
        `agm-responses/${user.uid}`,
      ) as DocumentReference<AGMResponse>;

      getDoc(responseDoc)
        .then((docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setExistingResponse(data);
            setAttending(data.attending);
            if (data.representativeName) {
              setRepresentativeName(data.representativeName);
            }
            if (data.representativeEmail) {
              setRepresentativeEmail(data.representativeEmail);
            }
            setIsSubmitted(true);
          }
        })
        .finally(() => {
          setIsLoadingResponse(false);
        });
    } else {
      setIsLoadingResponse(false);
    }
  }, [user, firestore]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!user) return;

    if (attending === "representative") {
      if (!representativeName.trim() || !representativeEmail.trim()) {
        alert("Please provide both name and email for your representative.");
        return;
      }
    }

    setIsSubmitting(true);

    const responseDoc = doc(
      firestore,
      `agm-responses/${user.uid}`,
    ) as DocumentReference<AGMResponse>;

    const now = Timestamp.now();
    const responseData: AGMResponse = {
      uid: user.uid,
      attending,
      createdAt: existingResponse?.createdAt || now,
      updatedAt: now,
    };

    if (attending === "representative") {
      responseData.representativeName = representativeName.trim();
      responseData.representativeEmail = representativeEmail.trim();
    }

    try {
      await setDoc(responseDoc, responseData);
      setIsSubmitted(true);
      setExistingResponse(responseData);
    } catch (error) {
      console.error("Error saving response:", error);
      alert("Failed to save your response. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isAuthenticating || isLoadingResponse) {
    return <Loader visible />;
  }

  if (authError) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <h2>Authentication Error</h2>
        <p>{authError}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <h2>Authentication Required</h2>
        <p>Please use the link from your email to access this page.</p>
      </div>
    );
  }

  return (
    <div id="wrapper">
      <Header dark />
      <section
        id="slider"
        className="slider-element"
        style={{
          backgroundColor: "#ECF4F1",
          padding: "100px 0",
          minHeight: "100vh",
        }}
      >
        <div
          className="shape-divider"
          data-shape="wave-6"
          data-height="350"
        ></div>

        <div className="container mw-sm" style={{ zIndex: 3 }}>
          <div className="text-center mb-5">
            <h1 className="display-4 fw-bold mb-3">Hitchhiker AGM 2026</h1>
            <p className="lead">Annual General Meeting</p>
          </div>
          <div
            className="card mb-4"
            style={{ backgroundColor: "white", borderRadius: "12px" }}
          >
            <div className="card-body p-4">
              <h4 className="mb-3">Your Attendance</h4>

              {isSubmitted && (
                <div className="alert alert-success mb-4" role="alert">
                  Your response has been recorded. You can update it below if
                  needed.
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="attendance"
                      id="attendSelf"
                      checked={attending === "self"}
                      onChange={() => setAttending("self")}
                    />
                    <label className="form-check-label" htmlFor="attendSelf">
                      I will attend personally
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="attendance"
                      id="attendRepresentative"
                      checked={attending === "representative"}
                      onChange={() => setAttending("representative")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="attendRepresentative"
                    >
                      I will nominate an authorized representative
                    </label>
                  </div>
                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="attendance"
                      id="notAttending"
                      checked={attending === "not-attending"}
                      onChange={() => setAttending("not-attending")}
                    />
                    <label className="form-check-label" htmlFor="notAttending">
                      I will not be attending
                    </label>
                  </div>
                </div>

                {attending === "representative" && (
                  <div
                    className="mb-4 p-3"
                    style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}
                  >
                    <h5 className="mb-3">Representative Details</h5>
                    <div className="mb-3">
                      <label htmlFor="repName" className="form-label">
                        Full Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="repName"
                        value={representativeName}
                        onChange={(e) => setRepresentativeName(e.target.value)}
                        placeholder="Enter representative's full name"
                        required={attending === "representative"}
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="repEmail" className="form-label">
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="repEmail"
                        value={representativeEmail}
                        onChange={(e) => setRepresentativeEmail(e.target.value)}
                        placeholder="Enter representative's email"
                        required={attending === "representative"}
                      />
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : isSubmitted
                      ? "Update Response"
                      : "Submit Response"}
                </button>
              </form>
            </div>
          </div>
          <div
            className="card"
            style={{ backgroundColor: "white", borderRadius: "12px" }}
          >
            <div className="card-body p-4">
              <h4 className="mb-3">Meeting Details</h4>
              <p className="mb-2">
                <strong>Date:</strong> Tuesday, June 16, 2026
              </p>
              <p className="mb-3">
                <strong>Time:</strong> 1:00 PM UTC
              </p>
              <p className="mb-3">
                The agenda will be sent out in the coming weeks.
              </p>

              <div className="table-responsive mb-4">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>Local Time</th>
                      <th>Timezone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TIMEZONES.map((tz) => (
                      <tr key={tz.location}>
                        <td>{tz.location}</td>
                        <td>{tz.time}</td>
                        <td>{tz.zone}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <a
                href={GOOGLE_CALENDAR_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary"
              >
                Add to Google Calendar
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
